/**
 * Préavis légal par ancienneté — Code du travail gabonais (loi 022/2021), art. 82.
 *
 * Le préavis dépend de l'ANCIENNETÉ et non de la catégorie (employé, maîtrise, cadre) :
 *   jusqu'à 1 an : 15 jours · 1 à 3 ans : 1 mois · 3 à 5 ans : 2 mois · 5 à 10 ans : 3 mois ·
 *   10 à 15 ans : 4 mois · 15 à 20 ans : 5 mois · 20 à 30 ans : 6 mois ·
 *   au-dessus de 30 ans : majoration de 10 jours par année de présence.
 * Ces durées sont des MINIMUMS : une convention collective peut être plus favorable (art. 82).
 * Art. 83 : licenciement économique = 3 mois minimum. Art. 81 : pas de préavis en cas de faute
 * lourde ou de force majeure.
 *
 * Lecture retenue pour « au-dessus de 30 ans » : 6 mois + 10 jours par année entière au-delà de 30
 * (le texte source est ambigu ; à valider par un juriste).
 */

export interface PreavisLegal {
  /** Durée exprimée dans `unite` (peut être fractionnaire au-delà de 30 ans). */
  duree: number;
  unite: 'jour' | 'mois';
  /** Durée convertie en mois (30 jours = 1 mois) pour valoriser l'indemnité compensatrice. */
  mois: number;
}

export function preavisLegalParAnciennete(anneesAnciennete: number): PreavisLegal {
  const a = Math.max(0, anneesAnciennete);
  if (a < 1) return { duree: 15, unite: 'jour', mois: 0.5 };
  const mois = a < 3 ? 1 : a < 5 ? 2 : a < 10 ? 3 : a < 15 ? 4 : a < 20 ? 5 : 6;
  if (a <= 30) return { duree: mois, unite: 'mois', mois };
  const joursEnPlus = Math.floor(a - 30) * 10;
  const total = 6 + joursEnPlus / 30;
  return { duree: total, unite: 'mois', mois: total };
}

/** Ancienneté en mois COMPLETS entre l'embauche et la sortie (ou aujourd'hui). */
export function ancienneteMoisComplets(dateEmbauche: string, dateSortie?: string): number {
  if (!dateEmbauche) return 0;
  const ref = dateSortie ?? new Date().toISOString().slice(0, 10);
  const [y1 = 0, m1 = 0, d1 = 0] = dateEmbauche.slice(0, 10).split('-').map(Number);
  const [y2 = 0, m2 = 0, d2 = 0] = ref.slice(0, 10).split('-').map(Number);
  if (!y1 || !y2) return 0;
  const mois = (y2 - y1) * 12 + (m2 - m1) - (d2 < d1 ? 1 : 0);
  return Math.max(0, mois);
}

/** Ancienneté en années (mois complets / 12), fractions comprises. */
export function ancienneteAnneesFractionnaires(dateEmbauche: string, dateSortie?: string): number {
  return ancienneteMoisComplets(dateEmbauche, dateSortie) / 12;
}
